import React, { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';
import { useData } from '../context/DataContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Newspaper, 
  Plus, 
  Trash2, 
  Edit3, 
  Image as ImageIcon, 
  Calendar, 
  Megaphone, 
  Mic2, 
  X,
  FileText,
  Tag,
  Eye
} from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, setDoc, doc, deleteDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';

const AdminNews = () => {
  const { data } = useData();
  const news = data.news || { latest: [], upcoming: [], press: [] };
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'latest' | 'upcoming' | 'press'
  const [editingItem, setEditingItem] = useState(null); // { section, data }
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (editingItem) {
      setUploadedImageUrl(editingItem.data?.image || '');
      document.body.classList.add('admin-modal-open');
    } else {
      document.body.classList.remove('admin-modal-open');
    }
    return () => document.body.classList.remove('admin-modal-open');
  }, [editingItem]);

  // Combine all news streams into a single list
  const allNews = [
    ...(news.latest || []).map(item => ({ ...item, section: 'latest' })),
    ...(news.upcoming || []).map(item => ({ ...item, section: 'upcoming' })),
    ...(news.press || []).map(item => ({ ...item, section: 'press' }))
  ];

  // Calculate stats for pill labels
  const totalCount = allNews.length;
  const latestCount = (news.latest || []).length;
  const upcomingCount = (news.upcoming || []).length;
  const pressCount = (news.press || []).length;

  // Filter based on active capsule pill selection
  const filteredNews = allNews.filter(item => {
    if (activeFilter === 'all') return true;
    return item.section === activeFilter;
  });

  const handleSaveItem = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const selectedSection = formData.get('section');
    const customCategory = formData.get('custom_category');
    const contentVal = formData.get('content') || '';
    const generatedDesc = contentVal.length > 150 ? contentVal.substring(0, 147) + '...' : contentVal;

    const itemData = {
      title: formData.get('title'),
      desc: generatedDesc,
      content: contentVal,
      date: formData.get('date'),
      image: uploadedImageUrl,
      section: selectedSection,
      category: customCategory || (selectedSection === 'latest' ? 'Production' : selectedSection === 'upcoming' ? 'Community' : 'Press Release')
    };

    setIsSaving(true);
    Swal.fire({
      title: 'Saving News...',
      text: 'Updating media center databases',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      if (editingItem.data?.id) {
        await setDoc(doc(db, 'news', editingItem.data.id), itemData);
      } else {
        await addDoc(collection(db, 'news'), itemData);
      }
      
      setIsSaving(false);
      await Swal.fire({
        title: 'Success',
        text: 'News item saved successfully',
        icon: 'success',
        confirmButtonColor: '#2d6a4f',
      });
      setEditingItem(null);
    } catch (error) {
      setIsSaving(false);
      console.error("Error saving news:", error);
      Swal.fire('Error', 'Failed to save news item', 'error');
    }
  };

  const handleDeleteItem = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This article will be permanently removed from active news & media pages!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete article'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'news', id));
        Swal.fire({
          title: 'Deleted!',
          text: 'News catalog entry removed.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire('Error', 'Failed to delete news item', 'error');
      }
    }
  };

  return (
    <div className="space-y-8 pb-20 font-poppins">
      
      {/* Header Banner */}
      <div className="bg-sugarcane-charcoal p-6 rounded-2xl text-white flex flex-col md:flex-row justify-between items-center gap-6 relative overflow-hidden shadow-md">
        <div className="absolute top-0 left-0 w-64 h-64 bg-sugarcane-green/10 rounded-full -ml-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-700/10 rounded-full -mr-32 -mb-32 blur-3xl"></div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-sugarcane-green rounded-xl flex items-center justify-center text-white">
              <Newspaper size={20} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Media Center</h2>
          </div>
          <p className="text-gray-400 text-sm italic">Distribute latest factory news, community updates, and event announcements.</p>
        </div>

        <div className="relative z-10 flex flex-wrap gap-4 items-center">
          <div className="px-5 py-2.5 bg-white/5 rounded-xl border border-white/10 flex items-center space-x-4">
            <div className="text-right">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sugarcane-green">Status</p>
              <p className="font-semibold text-sm">Online & Active</p>
            </div>
            <div className="w-2.5 h-2.5 bg-sugarcane-green rounded-full animate-pulse"></div>
          </div>
          <button
            onClick={() => setEditingItem({ section: activeFilter === 'all' ? 'latest' : activeFilter, data: {} })}
            className="bg-sugarcane-green hover:bg-white hover:text-sugarcane-charcoal text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center space-x-2 transition-all shadow-md"
          >
            <Plus size={18} />
            <span>New Article</span>
          </button>
        </div>
      </div>

      {/* Capsule Filter Pills Switcher */}
      <div className="flex bg-gray-100/80 p-1 rounded-2xl max-w-2xl mx-auto border border-gray-200/40 shadow-inner backdrop-blur-md">
        {[
          { id: 'all', label: 'All Media', count: totalCount },
          { id: 'latest', label: 'Latest Updates', count: latestCount },
          { id: 'upcoming', label: 'Upcoming Events', count: upcomingCount },
          { id: 'press', label: 'Press Releases', count: pressCount }
        ].map((tab) => {
          const isActive = activeFilter === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={`flex-1 py-2.5 rounded-xl font-bold uppercase text-[10px] tracking-wider transition-all duration-300 flex items-center justify-center space-x-2 ${
                isActive 
                  ? 'bg-sugarcane-green text-white shadow-md shadow-sugarcane-green/10' 
                  : 'text-gray-400 hover:text-sugarcane-charcoal'
              }`}
            >
              <span>{tab.label}</span>
              <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[8px] font-black tracking-normal ${
                isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-400'
              }`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* News Grid (Modern visual card structures with high premium feels) */}
      {filteredNews.length === 0 ? (
        <div className="bg-white py-20 text-center max-w-sm mx-auto rounded-2xl shadow-sm border border-gray-100">
          <div className="bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100 shadow-inner">
            <Newspaper className="text-gray-300" size={28} />
          </div>
          <h4 className="text-md font-bold text-sugarcane-charcoal mb-1">No Articles Published</h4>
          <p className="text-xs text-gray-400 leading-relaxed px-6">
            There are currently no articles in the "{activeFilter.toUpperCase()}" category. Add one using the button above.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredNews.map((item) => {
              // Custom category specific styles
              let badgeText = 'News';
              let badgeColor = 'bg-blue-50 text-blue-600 border-blue-100';
              let SectionIcon = Megaphone;

              if (item.section === 'upcoming') {
                badgeText = 'Event';
                badgeColor = 'bg-purple-50 text-purple-600 border-purple-100';
                SectionIcon = Calendar;
              } else if (item.section === 'press') {
                badgeText = 'Press';
                badgeColor = 'bg-orange-50 text-orange-600 border-orange-100';
                SectionIcon = Mic2;
              }

              return (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  whileHover={{ y: -4 }}
                  transition={{ type: 'tween', duration: 0.3 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between group overflow-hidden relative hover:-translate-y-1 hover:shadow-md hover:border-sugarcane-green/30 transition-all duration-300"
                >
                  <div className="space-y-4">
                    {/* Media Thumbnail Container with overlay control and scale-up effect */}
                    <div className="w-full h-44 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 shadow-sm relative group/img">
                      <img 
                        src={item.image || 'https://via.placeholder.com/400x300'} 
                        alt={item.title} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                      />
                      
                      {/* Hover action shortcuts overlay */}
                      <div className="absolute top-4 right-4 flex space-x-1.5 opacity-0 group-hover:opacity-100 group-hover/img:opacity-100 transition-opacity duration-300 z-10">
                        <button 
                          onClick={() => setEditingItem({ section: item.section, data: item })} 
                          className="p-2 bg-white/95 backdrop-blur-md text-blue-500 hover:bg-blue-500 hover:text-white rounded-lg transition-all shadow-md flex items-center justify-center border border-gray-100"
                          title="Edit Media"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button 
                          onClick={() => handleDeleteItem(item.id)} 
                          className="p-2 bg-white/95 backdrop-blur-md text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-all shadow-md flex items-center justify-center border border-gray-100"
                          title="Delete Media"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2.5">
                      <div className="flex justify-between items-center">
                        <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${badgeColor} flex items-center gap-1`}>
                          <SectionIcon size={10} />
                          {badgeText}
                        </span>
                        <span className="text-[10px] text-gray-400 font-semibold flex items-center gap-1">
                          <Calendar size={10} className="text-gray-400" />
                          {item.date}
                        </span>
                      </div>

                      <h4 className="text-md font-bold text-sugarcane-charcoal line-clamp-2 leading-snug group-hover:text-sugarcane-green transition-colors">
                        {item.title}
                      </h4>
                      
                      <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">
                        {item.desc}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                      <Tag size={10} className="text-gray-300" />
                      Class: <span className="text-sugarcane-charcoal font-bold">{item.category || 'General'}</span>
                    </span>
                    <button
                      onClick={() => setEditingItem({ section: item.section, data: item })}
                      className="text-[10px] font-bold text-sugarcane-green hover:text-sugarcane-charcoal transition-colors uppercase tracking-wider flex items-center space-x-1"
                    >
                      <span>Manage Article</span>
                      <span>&rarr;</span>
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Unified Media Post Editor Custom Modal Dialog Overlay */}
      <AnimatePresence>
        {editingItem && (
          <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-sugarcane-charcoal/80 backdrop-blur-md admin-modal-overlay">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-2xl relative border border-gray-100"
            >
              {/* Dark Gradient Header Banner */}
              {/* Sticky Header Banner */}
              <div className="sticky top-0 bg-white/95 backdrop-blur-md px-6 py-5 border-b border-gray-100 flex justify-between items-center z-10">
                <div>
                  <span className="px-3 py-1 bg-sugarcane-green/5 rounded-full text-[9px] font-bold uppercase tracking-widest text-sugarcane-green border border-sugarcane-green/10">
                    Media Registry
                  </span>
                  <h3 className="text-xl font-bold mt-1.5 tracking-tight text-sugarcane-charcoal">
                    {editingItem.data?.id ? 'Edit Media Post' : 'Create New Media Post'}
                  </h3>
                </div>
                <button 
                  onClick={() => setEditingItem(null)} 
                  className="p-2 bg-gray-50 hover:bg-gray-100 rounded-xl text-sugarcane-charcoal transition-colors cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Overhauled Balanced Form details */}
              <form onSubmit={handleSaveItem} className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-stretch">
                  
                  {/* Left Column (Balanced Image Upload & Meta Details) - lg:col-span-2 */}
                  <div className="lg:col-span-2 space-y-6">
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 flex flex-col justify-center items-center shadow-sm">
                      <div className="w-full max-w-[280px] mx-auto space-y-4">
                        <div className="text-center mb-2">
                          <label className="block text-xs font-bold uppercase tracking-wider text-gray-400">Media Thumbnail</label>
                          <p className="text-[10px] text-gray-400 mt-1 font-medium">Recommended 4:3 format</p>
                        </div>
                        
                        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-white">
                          <ImageUpload onUploadSuccess={setUploadedImageUrl} initialImage={uploadedImageUrl} />
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 space-y-5 shadow-sm">
                      <h4 className="text-xs font-black text-sugarcane-charcoal uppercase tracking-wider border-b border-gray-200 pb-2">Publishing Meta</h4>
                      
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Media Stream Section</label>
                        <select 
                          name="section" 
                          defaultValue={editingItem.section || 'latest'} 
                          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold text-sugarcane-charcoal focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none cursor-pointer shadow-inner"
                        >
                          <option value="latest">Latest Updates</option>
                          <option value="upcoming">Upcoming Events</option>
                          <option value="press">Press Releases</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Display Date</label>
                        <input 
                          name="date" 
                          defaultValue={editingItem.data?.date} 
                          placeholder="e.g. May 2026" 
                          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold text-sugarcane-charcoal focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none shadow-inner" 
                          required 
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Custom Sub-category Label (optional)</label>
                        <input 
                          name="custom_category" 
                          defaultValue={editingItem.data?.category} 
                          placeholder="e.g. Production / Event / Announcement" 
                          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold text-sugarcane-charcoal focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none shadow-inner" 
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right Column (Title & Detailed Content inputs) - lg:col-span-3 */}
                  <div className="lg:col-span-3 space-y-6">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Article Title</label>
                      <input 
                        name="title" 
                        defaultValue={editingItem.data?.title} 
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold text-sugarcane-charcoal focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none shadow-inner" 
                        placeholder="e.g. Record Sugar Production Achieved in Fiscal 2026" 
                        required 
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Detailed Article Content</label>
                      <textarea 
                        name="content" 
                        defaultValue={editingItem.data?.content || editingItem.data?.desc} 
                        rows="15"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold text-sugarcane-charcoal focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none leading-relaxed shadow-inner resize-none" 
                        placeholder="Provide complete deep-dive content representing full post parameters..."
                        required 
                      />
                    </div>
                  </div>

                </div>

                {/* Footer action button arrays */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                  <button 
                    type="button" 
                    onClick={() => setEditingItem(null)} 
                    className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-bold text-sm transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={isSaving}
                    className="px-6 py-2.5 bg-sugarcane-green hover:bg-sugarcane-charcoal text-white rounded-xl font-bold text-sm transition-all shadow-md shadow-sugarcane-green/10 flex items-center gap-2 cursor-pointer"
                  >
                    {isSaving ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <Plus size={16} />
                    )}
                    <span>{isSaving ? 'Publishing Article...' : (editingItem.data?.id ? 'Save Changes' : 'Publish Article')}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default AdminNews;
